import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isEqual, numericSort } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { requestSuggestions } from 'src/actions/data-sources/action-creators/requestSuggestions';
import { requestVoiceboxSuggestions } from 'src/actions/data-sources/action-creators/requestVoiceboxSuggestions';
import { dataSourcesActionCreators } from 'src/actions/data-sources/actionCreators';
import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { useCompleteRelationshipMappingsForClassMapping } from 'src/containers/ProjectDiagram/hooks/useCompleteRelationshipMappingsForClassMapping';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingIdentifierPart,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { createDraftData } from 'src/containers/ProjectDiagram/utils/draft/createDraftData';
import {
  getAllDomainsAndRanges,
  isValidMappingDomainAndRange,
} from 'src/containers/ProjectDiagram/utils/isValidDomainAndRange';
import { addMappingField } from 'src/containers/ProjectDiagram/utils/mapping/addMappingField';
import { addRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/addRelationshipMapping';
import { setMappingLabel } from 'src/containers/ProjectDiagram/utils/mapping/setMappingLabel';
import { setMappingPrimaryIdentifier } from 'src/containers/ProjectDiagram/utils/mapping/setMappingPrimaryIdentifier';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { useAttributeFieldsContext } from 'src/containers/ProjectMapping/hooks/attributes';
import { DEFAULT_DELIMITER } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import { useRelationshipMappingsFromSuggestions } from 'src/containers/ProjectMapping/hooks/relationshipSuggestions';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import {
  AttributeSuggestion,
  LabelSuggestion,
  PrimaryIdentifierSuggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { Connection } from 'src/store/connections/types';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

// callbacks

export const useSuggestMappings = ({
  classIri,
  connection,
}: {
  classIri: string;
  connection: Connection | null;
}) => {
  const diagram = useDiagram();
  const isMounted = useIsMounted();
  const mapping = useMappingFromRoute();
  const { setLoadingMappingId } = useSuggestionContext();
  const dispatch = useReduxDispatch();
  const { mappingId, projectId } = useRouterParams<'PROJECT_MAPPING'>();

  const { fieldValuesByAttribute } = useAttributeFieldsContext();
  const resourceData = useReduxSelector(
    projectNodeDataSelector(projectId, mapping?.from, isResource),
    isEqual
  );

  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const resourceId = resourceData?.value;
  const createRelationshipMappings =
    useRelationshipMappingsFromSuggestions(resourceId);

  const loadingKey = React.useRef('');

  const handleCompleteRelationshipMappings =
    useCompleteRelationshipMappingsForClassMapping();

  const suggestMappings = React.useCallback(async () => {
    if (!connection || !resourceId || !mappingId) {
      return;
    }

    setLoadingMappingId(mappingId);
    const currentLoadingKey = uuid();
    loadingKey.current = currentLoadingKey;

    const suggestions = useVoiceboxSuggestions
      ? await dispatch(
          requestVoiceboxSuggestions({
            classIris: [classIri],
            connection,
            projectId,
            resourceIds: [resourceId],
          })
        )
      : await dispatch(
          requestSuggestions({
            classIris: [classIri],
            connection,
            projectId,
            resourceIds: [resourceId],
          })
        );

    if (!isMounted.current) {
      return;
    }

    if (!diagram || !mapping?.key) {
      return;
    }

    if (suggestions) {
      const existingAttributeIris = Object.values(mapping.fields).reduce<
        Set<string>
      >((acc, field) => {
        field.targets.forEach((target) => {
          acc.add(target);
        });
        return acc;
      }, new Set());

      const suggestionByAttribute = Object.entries(
        fieldValuesByAttribute
      ).reduce<Record<string, AttributeSuggestion | null>>(
        (acc, [attributeIri, values]) => {
          // skip attributes with fields already defined
          if (existingAttributeIris.has(attributeIri)) {
            return acc;
          }

          acc[attributeIri] =
            values
              .map((value) =>
                value
                  ? null
                  : suggestions.find(
                      (suggestion): suggestion is AttributeSuggestion =>
                        suggestion.type === SuggestionType.ATTRIBUTE &&
                        suggestion.iri === attributeIri
                    ) || null
              )
              .filter((item): item is AttributeSuggestion => Boolean(item))
              .sort(numericSort((item) => item.score, true))[0] || null;
          return acc;
        },
        {}
      );

      const labelSuggestion =
        (!mapping?.label &&
          suggestions
            .filter(
              (suggestion): suggestion is LabelSuggestion =>
                suggestion.type === SuggestionType.LABEL &&
                suggestion.resource.id === resourceId
            )
            .sort(numericSort((item) => item.score, true))[0]) ||
        null;

      const primaryIdentifierSuggestion =
        (!mapping?.primaryIdentifier.parts.length &&
          suggestions
            .filter(
              (suggestion): suggestion is PrimaryIdentifierSuggestion =>
                suggestion.type === SuggestionType.PRIMARY_IDENTIFIER &&
                suggestion.resource.id === resourceId
            )
            .sort(numericSort((item) => item.score, true))[0]) ||
        null;

      const relationshipMappings = createRelationshipMappings(suggestions);

      const hasSuggestions = Boolean(
        labelSuggestion ||
          primaryIdentifierSuggestion ||
          relationshipMappings.length ||
          Object.values(suggestionByAttribute).find(
            (suggestion) => suggestion !== null
          )
      );

      let didAddRelationshipMappings = false;

      if (!diagram.model.modelData.draftData) {
        // clear undo history for new draft
        diagram.undoManager.clear();
      }
      commitDiagramModel(
        diagram,
        (model) => {
          // setup draft data
          if (!model.modelData.draftData) {
            const draftData = createDraftData(model);
            setDataProperty<ModelData>(model)(
              model.modelData,
              'draftData',
              draftData
            );
          }

          // accept new attribute suggestions
          Object.entries(suggestionByAttribute).forEach(
            ([attributeIri, suggestion]) => {
              if (suggestion?.field) {
                addMappingField({
                  attributeIri,
                  fieldId: suggestion.field,
                  key: mapping.key,
                })(model);
              }
            }
          );

          // accept new label suggestion
          if (labelSuggestion) {
            setMappingLabel({
              key: mapping.key,
              label: labelSuggestion.field,
            })(model);
          }

          // accept new primary identifier suggestion
          if (primaryIdentifierSuggestion) {
            const parts: MappingIdentifierPart[] = [
              ...mapping.primaryIdentifier.parts,
            ];
            primaryIdentifierSuggestion.fields.forEach((item) => {
              if (
                item.type === MappingIdentifierType.FIELD &&
                parts.length &&
                parts[parts.length - 1].type === MappingIdentifierType.FIELD
              ) {
                parts.push({
                  type: MappingIdentifierType.DELIMITER,
                  value: DEFAULT_DELIMITER,
                });
              }
              parts.push(item);
            });

            setMappingPrimaryIdentifier({
              key: mapping.key,
              primaryIdentifier: {
                ...mapping.primaryIdentifier,
                parts,
              },
            })(model);
          }

          const { classMappingIriByKey, domainsAndRanges } =
            getAllDomainsAndRanges(model);
          relationshipMappings.forEach((option) => {
            if (
              !isValidMappingDomainAndRange(
                option,
                classMappingIriByKey,
                domainsAndRanges
              )
            ) {
              console.warn(
                `Skipping create invalid relationship mapping (iri: ${
                  option.relationshipIri
                }, from: ${
                  option.from
                    ? classMappingIriByKey.get(option.from)
                    : option.fromClassIri
                }, to: ${
                  option.to
                    ? classMappingIriByKey.get(option.to)
                    : option.toClassIri
                })`
              );
              return;
            }

            // accept new relationship mappings
            addRelationshipMapping(option)(model);
          });

          didAddRelationshipMappings =
            handleCompleteRelationshipMappings(mapping)(model);

          // clear draft data if there were no changes
          if (!hasSuggestions && !didAddRelationshipMappings) {
            setDataProperty<ModelData>(model)(
              model.modelData,
              'draftData',
              undefined
            );
          }
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );

      if (!hasSuggestions && !didAddRelationshipMappings) {
        dispatch(dataSourcesActionCreators.noSuggestionsFound());
      } else {
        dispatch(projectsActionCreators.addedSuggestionsToProject(projectId));
      }
    }

    if (loadingKey.current === currentLoadingKey) {
      setLoadingMappingId('');
    }
  }, [
    classIri,
    connection,
    createRelationshipMappings,
    diagram,
    dispatch,
    fieldValuesByAttribute,
    handleCompleteRelationshipMappings,
    isMounted,
    mapping,
    mappingId,
    projectId,
    resourceId,
    setLoadingMappingId,
    useVoiceboxSuggestions,
  ]);

  return suggestMappings;
};
